import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { getBlob } from '../cache';
import { FADE_TRANSITION_MS } from '../config';
// M2E.C: ItemView only handles MEDIA items (IMAGE/VIDEO). MENU_BOARD/PROMO
// render code lands in M2E.D; Player.tsx filters them out upstream.
import type { ManifestMediaItem } from '../types';

interface ItemViewProps {
  item: ManifestMediaItem;
  fadingOut?: boolean;
  onEnd: () => void;
}

// Renders a single manifest item (image or video) loaded from the IDB cache
// by sha256. Manages its own blob URL lifecycle (create on mount, revoke on
// unmount). The `fadingOut` prop is the upstream signal that this layer is
// being replaced — when true, the layer transitions to opacity 0 and does
// NOT advance the parent (its replacement handles that).
export function ItemView({ item, fadingOut = false, onEnd }: ItemViewProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Load blob from IDB and create an object URL. Strict invariant: blob URL
  // is revoked on unmount to avoid memory leaks across long kiosk runs.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    void (async () => {
      try {
        const blob = await getBlob(item.sha256);
        if (cancelled || !blob) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch {
        // Silent — sync layer will retry on the next poll.
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.sha256]);

  // Image duration timer. Skipped for video (the <video> element drives end
  // via 'ended' event + the explicit cap timer below).
  useEffect(() => {
    if (fadingOut || !blobUrl || item.kind !== 'IMAGE') return;
    const t = window.setTimeout(onEnd, item.durationMs);
    return () => clearTimeout(t);
  }, [blobUrl, fadingOut, item.durationMs, item.kind, onEnd]);

  if (!blobUrl) return null;

  const style: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: '#000',
    opacity: fadingOut ? 0 : 1,
    transition: `opacity ${FADE_TRANSITION_MS}ms ease-in-out`,
  };

  if (item.kind === 'IMAGE') {
    return <img src={blobUrl} alt="" draggable={false} style={style} />;
  }
  return (
    <VideoLayer
      url={blobUrl}
      durationMs={item.durationMs}
      style={style}
      onEnd={fadingOut ? null : onEnd}
    />
  );
}

interface VideoLayerProps {
  url: string;
  durationMs: number;
  style: CSSProperties;
  onEnd: (() => void) | null;
}

// Video advances on the earlier of: native 'ended' event OR durationMs cap.
// Muted is REQUIRED for browser autoplay; playsInline keeps it from going
// fullscreen on iOS Safari.
function VideoLayer({ url, durationMs, style, onEnd }: VideoLayerProps) {
  useEffect(() => {
    if (!onEnd || durationMs <= 0) return;
    const t = window.setTimeout(onEnd, durationMs);
    return () => clearTimeout(t);
  }, [durationMs, onEnd]);

  return (
    <video
      src={url}
      autoPlay
      muted
      playsInline
      onEnded={onEnd ?? undefined}
      style={style}
    />
  );
}
